import React, { useEffect, useState } from "react";
import {
  BottomSheet,
  Button,
  EmailChips,
  Modal,
  fieldLabelClasses,
  useIsMobile,
  useToast,
} from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { fetchClient } from "../../clients/services/clientsService";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { savePlanPdf } from "../planDocument";
import { formatPlanDateLong } from "../planMapping";
import { fetchMealPlanPdf } from "../services/mealPlansService";
import type { PlanRecord } from "../types/plans.types";

/**
 * What this dialog needs of a plan, which is less than a whole one.
 *
 * The builder opens it over the `PlanRecord` it is holding; a phone opens it
 * over the row in the client's plan list, which is all that list fetched. The
 * two differ in one field — a row carries no `code` — so the dialog asks for
 * the fields it reads rather than for a record, and the Plan code line is
 * drawn only when there is a number to draw. Everything else, including the
 * document the Download button fetches, is reached by `id`.
 */
export type SendablePlan = Pick<
  PlanRecord,
  "id" | "clientId" | "clientName" | "planType" | "name"
> & {
  /** The plan's own number, `NMP004` — absent on a list row. */
  code?: string;
  /** ISO, and `null` on a plan the backend gave no date for. */
  createdOn: string | null;
};

export interface SendPlanModalProps {
  plan: SendablePlan;
  onClose: () => void;
  /**
   * Given the addresses it is going to — sending it is the caller's to do.
   *
   * Awaited: the dialog stands with its Send button working while the call is
   * in flight, and stays open if it is refused, so the addresses that were
   * typed are still there to try again with. Closing it is the caller's too,
   * once the plan has actually gone.
   */
  onSend: (emails: string[]) => void | Promise<void>;
}

/**
 * What the plan is, and where it is about to go.
 *
 * The details are shown rather than asked for: everything bar the address is
 * already settled by the time this opens, and reading it back is the last
 * chance to notice the plan was written for the wrong client.
 */
const SendPlanModal: React.FC<SendPlanModalProps> = ({
  plan,
  onClose,
  onSend,
}) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const toast = useToast();
  const getToken = useTokenGetter();
  const isMobile = useIsMobile();

  /**
   * The client's own address, filled in from their record.
   *
   * Fetched rather than carried on the plan: a plan knows who it is for and
   * what they are called, and nothing more about them. It arrives as a chip
   * like any other — removable, and joined by whoever else the plan is being
   * copied to — so the box is a starting point rather than an answer.
   *
   * A client with no address on record, or a lookup that fell over, simply
   * leaves it empty: an address that could not be filled in is a convenience
   * that was not there, not a failure worth a message. Anything already typed
   * wins, since the fetch may land after someone has started.
   */
  useEffect(() => {
    if (!plan.clientId) return;

    const controller = new AbortController();

    fetchClient(getToken, plan.clientId, controller.signal)
      .then((client) => {
        const email = client.email?.trim();
        if (controller.signal.aborted || !email) return;
        setEmails((current) => (current.length === 0 ? [email] : current));
      })
      .catch(() => {});

    return () => controller.abort();
  }, [getToken, plan.clientId]);

  /** What a failed request tells the user, in the words the API used. */
  const reportFailure = (what: string, reason: unknown) =>
    toast.error(
      what,
      reason instanceof ApiError
        ? reason.message
        : "We could not reach the server. Check your connection and try again."
    );

  /**
   * The plan as a file. The backend renders it, so this waits on a request
   * rather than building one — hence a button that can be in flight.
   */
  const handleDownload = async () => {
    setDownloading(true);
    try {
      savePlanPdf(plan, await fetchMealPlanPdf(getToken, plan.id));
    } catch (reason) {
      reportFailure("Could not download the plan", reason);
    } finally {
      setDownloading(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await onSend(emails);
    } finally {
      // Whatever happened, the button is a button again. The dialog itself is
      // only closed by the caller, and only once the plan has gone.
      setSending(false);
    }
  };

  const details: Array<[label: string, value: string]> = [
    ["Client name", plan.clientName],
    ["Goal", plan.planType],
    ["Plan name", plan.name],
    // Only where the plan is in hand whole. A row that does not carry the
    // number draws no line for it rather than a line reading "Plan code : -",
    // which would say the plan has not got one.
    ...(plan.code
      ? ([["Plan code", plan.code]] as Array<[string, string]>)
      : []),
    ["Created date", formatPlanDateLong(plan.createdOn)],
  ];

  const download = (
    <button
      type="button"
      onClick={handleDownload}
      disabled={downloading}
      className="rounded-md px-1 py-1 text-[14px] text-brand transition-colors hover:text-brand-dark hover:underline disabled:cursor-not-allowed disabled:text-ink-subtle disabled:no-underline"
    >
      {/* Said rather than spun: the label is the only thing in this corner of
          the dialog, so there is room to say what is happening. */}
      {downloading ? "Preparing…" : "Download"}
    </button>
  );

  const cancel = (
    <Button variant="secondary" size="md" onClick={onClose}>
      Cancel
    </Button>
  );

  const send = (
    <Button
      size="md"
      // Nowhere to send it to is not a send.
      disabled={emails.length === 0}
      loading={sending}
      onClick={handleSend}
    >
      Send
    </Button>
  );

  const body = (
    <>
      {/* Label, colon and value in three columns, so the values line up down
          the dialog however long the labels get. On a phone the first column
          gives up what it can spare: a 360px screen has 250px left for a
          client's name once the label and the colon have taken theirs. */}
      <dl className="grid grid-cols-[88px_10px_1fr] md:grid-cols-[100px_10px_1fr] gap-y-3 text-[14px]">
        {details.map(([label, value]) => (
          <React.Fragment key={label}>
            <dt className="text-ink-muted">{label}</dt>
            <dd aria-hidden="true" className="text-ink-muted">
              :
            </dd>
            <dd className="break-words text-ink">{value}</dd>
          </React.Fragment>
        ))}
      </dl>

      {/* On a phone the file sits here rather than in the row of buttons.
          That row is two answers to "send this or not", and a third control
          that is neither — in a different shape, doing a different thing —
          read as a button somebody had failed to line up. Here it is beside
          the plan it would download, which is what it is about. */}
      {isMobile && <div className="mt-4 flex justify-end">{download}</div>}

      <div className="mt-5 border-t border-line pt-4">
        <p className={`${fieldLabelClasses} mb-1.5`}>Client email</p>
        <EmailChips emails={emails} onChange={setEmails} />
      </div>
    </>
  );

  if (isMobile) {
    return (
      // The drawer a phone opens this in — the plan list at this width has one
      // button per card, and what it opens comes up under the thumb that
      // pressed it rather than landing in the middle of the screen. See
      // `BottomSheet`.
      <BottomSheet
        open
        title="Send plan to client"
        onClose={onClose}
        footer={
          // The two answers side by side, each stretched to its share of the
          // row — what every other sheet in the app puts under a question,
          // and what `BottomSheet`'s own footer is built to lay out. Download
          // is not one of the two and sits in the body above; see there.
          <>
            {cancel}
            {send}
          </>
        }
      >
        <div className="px-5">{body}</div>
      </BottomSheet>
    );
  }

  return (
    <Modal
      open
      title="Send plan to client"
      onClose={onClose}
      className="max-w-[526px]"
      footer={
        <>
          <span className="mr-auto">{download}</span>
          {cancel}
          {send}
        </>
      }
    >
      {body}
    </Modal>
  );
};

export default SendPlanModal;
